import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { Initials, Logo, TeamAvatar } from '../components/ui';
import { useMe, useSession } from '../lib/session';
import { useFrameworkQueueCount } from '../lib/frameworks';
import './AppLayout.css';

const ROLE_LABEL: Record<string, string> = {
  teacher: 'Teacher',
  school_leader: 'Leader',
  hybrid: 'Leader',
  system_admin: 'EducAitor admin',
};

/**
 * `also`: other paths that belong to the same item (it stays highlighted there).
 * `channel`: the Community page opened on the EducAitor channel (?space=educaitor).
 */
type NavItem = { to: string; label: string; end?: boolean; also?: string; channel?: boolean };

// What EducAitor staff run for every organisation.
const ADMIN_NAV: NavItem[] = [
  { to: '/admin/schools', label: 'Organisations' },
  { to: '/community?space=educaitor', label: 'EducAitor channel', channel: true },
  { to: '/admin/frameworks', label: 'Framework library', also: '/admin/framework-requests' },
  { to: '/admin/reflection-models', label: 'Reflection models' },
  { to: '/admin/health', label: 'Platform health' },
];

const SCHOOL_NAV: NavItem[] = [
  { to: '/', label: 'Overview', end: true },
  { to: '/hub', label: 'Leadership Hub' },
  { to: '/development', label: 'Development' },
  { to: '/community', label: 'Community' },
  { to: '/shared', label: 'Shared with you' },
  { to: '/people', label: 'People' },
  { to: '/frameworks', label: 'Frameworks' },
  { to: '/school', label: 'Organisation settings' },
];

/**
 * Sidebar on desktop, a top bar on phones. The profile button opens a menu
 * (upwards in the sidebar, downwards on phones) with Account and Sign out.
 */
export function AppLayout() {
  const me = useMe();
  const { signOut } = useSession();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const meRef = useRef<HTMLDivElement>(null);
  const isAdmin = me.role === 'system_admin';
  const frameworksWaiting = useFrameworkQueueCount(isAdmin);
  // Community is one page; for admins its EducAitor tab is highlighted as "EducAitor channel".
  const onCommunity = location.pathname === '/community';
  const inChannel = isAdmin && onCommunity && new URLSearchParams(location.search).get('space') === 'educaitor';

  useEffect(() => {
    setMenuOpen(false);
    setNavOpen(false);
  }, [location.pathname]);

  // While the phone menu is open the page behind it stays put, and Escape closes it.
  useEffect(() => {
    if (!navOpen) return;
    // iOS Safari ignores overflow:hidden for touch scrolling, so pin the page
    // where it is and put it back when the menu closes.
    const body = document.body;
    const y = window.scrollY;
    const prev = { position: body.style.position, top: body.style.top, width: body.style.width };
    body.style.position = 'fixed';
    body.style.top = `-${y}px`;
    body.style.width = '100%';
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setNavOpen(false);
    document.addEventListener('keydown', esc);
    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.width = prev.width;
      window.scrollTo(0, y);
      document.removeEventListener('keydown', esc);
    };
  }, [navOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: MouseEvent) => {
      if (!meRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [menuOpen]);

  return (
    <div className="shell">
      <aside className={`shell-nav${navOpen ? ' is-open' : ''}`}>
        <div className="shell-brand">
          <Link to="/" aria-label="EducAitor home">
            <Logo height={26} />
          </Link>
          <button
            className="shell-burger"
            aria-label={navOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={navOpen}
            aria-controls="main-nav"
            onClick={() => {
              setMenuOpen(false);
              setNavOpen((v) => !v);
            }}
          >
            <span />
            <span />
          </button>
        </div>

        <nav aria-label="Main" className="shell-links" id="main-nav">
          {/* Admins see what they run for everyone first, then their own organisation, as two clearly labelled groups. */}
          {isAdmin ? (
            <div className="shell-section" role="group" aria-labelledby="nav-admin">
              <p className="shell-group" id="nav-admin">
                EducAitor admin
                <span>Every organisation</span>
              </p>
              {ADMIN_NAV.map((n) => {
                const count = n.to === '/admin/frameworks' ? frameworksWaiting : 0;
                const alsoHere = !!n.also && location.pathname.startsWith(n.also);
                const here = n.channel ? inChannel : alsoHere;
                return (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    className={({ isActive }) =>
                      [(n.channel ? inChannel : isActive || alsoHere) ? 'active' : '', count ? 'has-count' : ''].filter(Boolean).join(' ') || undefined
                    }
                    aria-current={here ? 'page' : undefined}
                  >
                    {n.label}
                    {count ? (
                      <span className="shell-count" aria-label={`${count} waiting`}>
                        {count}
                      </span>
                    ) : null}
                  </NavLink>
                );
              })}
            </div>
          ) : null}
          <div className="shell-section" role="group" aria-labelledby="nav-org">
            <p className="shell-group" id="nav-org">
              {me.school_name ?? 'Your organisation'}
              {isAdmin ? <span>Your organisation</span> : null}
            </p>
            {SCHOOL_NAV.map((n) =>
              n.to === '/community' ? (
                // The organisation feed; the EducAitor channel has its own item for admins.
                <NavLink key={n.to} to={n.to} className={() => (onCommunity && !inChannel ? 'active' : '')}
                  aria-current={onCommunity && !inChannel ? 'page' : undefined}
                >
                  {n.label}
                </NavLink>
              ) : (
                <NavLink key={n.to} to={n.to} end={n.end}>
                  {n.label}
                </NavLink>
              ),
            )}
          </div>
        </nav>

        <div className="shell-me" ref={meRef}>
          <button
            className="shell-me-button"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => {
              setNavOpen(false);
              setMenuOpen((v) => !v);
            }}
          >
            {me.role === 'system_admin' ? <TeamAvatar size={34} /> : <Initials name={me.name} size={34} />}
            <span className="shell-me-text">
              <span className="shell-me-name">{me.name}</span>
              <span className="shell-me-role">{ROLE_LABEL[me.role] ?? me.role}</span>
            </span>
            <svg className="shell-me-chevron" width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M7 10l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {menuOpen ? (
            <div className="shell-me-menu" role="menu">
              <div className="shell-me-head">
                <p className="shell-me-name">{me.name}</p>
                <p className="shell-me-email">{me.email}</p>
              </div>
              <Link role="menuitem" to="/account">
                Your account
              </Link>
              <Link role="menuitem" to="/school">
                Organisation settings
              </Link>
              <button role="menuitem" className="shell-signout" onClick={() => signOut()}>
                Sign out
              </button>
            </div>
          ) : null}
        </div>
      </aside>
      <main className="shell-main" id="main">
        <Outlet />
      </main>
    </div>
  );
}
